/* oxlint-disable jsx-a11y/prefer-tag-over-role */
'use client';

import React, { useId, useEffect } from 'react';

import { cn } from 'cn';
import {
  motion,
  useAnimationFrame,
  useMotionValue,
  useSpring,
} from 'framer-motion';
import { useTranslations } from 'use-intl';

export type VoiceOrbState =
  | 'ai-talking'
  | 'ai-thinking'
  | 'user-talking'
  | 'user-idle';

interface Props {
  state: VoiceOrbState;
  audioLevel: number;
  progress?: number;
  className?: string;
  compact?: boolean;
  size?: number;
}

const BACKGROUND_COLORS: Record<VoiceOrbState, string> = {
  'ai-talking': '#F6316C',
  'ai-thinking': '#A2BBF6',
  'user-talking': '#ffffff',
  'user-idle': '#A2BBF6',
};

function buildWavePath(
  phase: number,
  amplitude: number,
  intensity: number,
  frequency: number,
  yOffset = 0
): string {
  const left = -10;
  const right = 110;
  // the middle of the wave is around y=60
  const centerY = 60 + yOffset;
  const segments = 60;

  const wavePoints: { x: number; y: number }[] = [];
  for (let i = 0; i <= segments; i++) {
    const x = left + (i / segments) * (right - left);
    const wave1 = Math.sin(
      (frequency * 2 * Math.PI * (x - left)) / (right - left) +
        phase * 2 * Math.PI
    );
    const wave2 = Math.sin(
      (frequency * 1.5 * Math.PI * (x - left)) / (right - left) +
        phase * 3 * Math.PI
    );

    const y = centerY + amplitude * intensity * ((wave1 + wave2 * 0.5) / 1.5);
    wavePoints.push({ x, y });
  }

  const bottomY = 120;
  return (
    `M ${wavePoints[0].x} ${wavePoints[0].y} ` +
    wavePoints.map((p) => `L ${p.x} ${p.y}`).join(' ') +
    ` L ${right} ${bottomY} L ${left} ${bottomY} Z`
  );
}

export function VoiceOrb({
  state,
  audioLevel,
  progress = 0,
  className,
  compact = false,
  size,
}: Props) {
  const id = useId().replace(/:/g, '-');
  const clipId = `orb-clip-${id}`;
  const gradId = `orb-grad-${id}`;

  const isTalkingProps = state === 'ai-talking' || state === 'user-talking';
  const baseAmplitude = isTalkingProps ? 3 : 1;
  const audioAmplitudeMult = 15;

  const time = useMotionValue(0);
  const smoothedAudio = useSpring(audioLevel, {
    stiffness: 80,
    damping: 20,
    mass: 1,
  });
  const intensity = useSpring(isTalkingProps ? 1 : 0.2, {
    stiffness: 60,
    damping: 25,
  });

  const path1 = useMotionValue('');
  const path2 = useMotionValue('');

  const t = useTranslations('Chat');

  useEffect(() => {
    smoothedAudio.set(audioLevel);
  }, [audioLevel, smoothedAudio]);

  useEffect(() => {
    intensity.set(isTalkingProps ? 1 : 0.2);
  }, [isTalkingProps, intensity]);

  useAnimationFrame((t, delta) => {
    // Slower speed
    const currentIntensity = intensity.get();
    const speed = 0.0002 + currentIntensity * 0.0003;
    time.set(time.get() + delta * speed);

    const phase = time.get();
    const currentAudio = smoothedAudio.get();

    const amplitude = baseAmplitude + currentAudio * audioAmplitudeMult;

    // Two slightly offset waves to make it feel more "liquid"
    const wave1 = buildWavePath(phase, amplitude, currentIntensity, 1.5, 0);
    const wave2 = buildWavePath(
      phase + 0.33,
      amplitude * 0.8,
      currentIntensity,
      2,
      2
    );

    path1.set(wave1);
    path2.set(wave2);
  });

  const bgColor = BACKGROUND_COLORS[state];

  const targetSize = size ?? (compact ? 48 : 120);

  return (
    <div
      className={cn('relative flex items-center justify-center', className)}
      data-test='voice-orb'
      data-compact={compact}
    >
      <motion.div
        className='relative overflow-hidden rounded-full'
        animate={{ width: targetSize, height: targetSize }}
        initial={false}
        transition={{ type: 'spring', stiffness: 220, damping: 26 }}
      >
        {/* Base Background Color */}
        <motion.div
          className='absolute inset-0'
          animate={{ backgroundColor: bgColor }}
          transition={{ duration: 0.8 }}
        />

        {/* The Wave Paths */}
        <svg
          className='absolute inset-0 size-full'
          viewBox='0 0 100 100'
          xmlns='http://www.w3.org/2000/svg'
        >
          <defs>
            <clipPath id={clipId}>
              <circle cx='50' cy='50' r='50' />
            </clipPath>

            <linearGradient id={gradId} x1='0' y1='0.17' x2='0' y2='1'>
              <stop stopColor='#C026D3' />
              <stop offset='1' stopColor='#316CF6' />
            </linearGradient>
          </defs>

          <g clipPath={`url(#${clipId})`}>
            <motion.path d={path2} fill={`url(#${gradId})`} opacity={0.6} />
            <motion.path d={path1} fill={`url(#${gradId})`} opacity={1} />
          </g>
        </svg>

        {/* SVG Blur Filter Layer */}
        <div className='absolute inset-0 rounded-full backdrop-blur-lg' />

        {/* Top Overlay Rect */}
        <div className='absolute inset-0 bg-rose-500 opacity-15' />

        {state === 'ai-thinking' &&
          (compact ? (
            <div
              className='absolute inset-0 flex items-center justify-center'
              data-test='voice-orb-thinking-dots'
              aria-label={t('thinking')}
              role='status'
            >
              <div className='flex items-center gap-1'>
                {[0, 1, 2].map((i) => (
                  <motion.span
                    key={i}
                    className='block size-1.5 rounded-full bg-white'
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      delay: i * 0.2,
                      ease: 'easeInOut',
                    }}
                  />
                ))}
              </div>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className='absolute inset-x-0 bottom-7 mx-auto flex w-max items-center justify-center rounded-full bg-white/20 px-2 py-1 backdrop-blur-md'
            >
              <span className='text-[10px] font-semibold tracking-wide text-white'>
                {t('thinking')}
              </span>
            </motion.div>
          ))}

        {/* Progress ring around the orb */}
        {progress > 0 && (
          <svg
            className='pointer-events-none absolute inset-0 size-full -rotate-90'
            viewBox='0 0 100 100'
            xmlns='http://www.w3.org/2000/svg'
          >
            <circle
              cx='50'
              cy='50'
              r='48'
              fill='none'
              stroke='white'
              strokeWidth='4'
              strokeDasharray={`${2 * Math.PI * 48 * progress} ${
                2 * Math.PI * 48
              }`}
              strokeDashoffset='0'
              strokeLinecap='round'
            />
          </svg>
        )}
      </motion.div>
    </div>
  );
}
